import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ToastService } from '../../../../../service/toast.service';
import { ScreenSizeService } from '../../../../../service/screen-size.service';
import { PhotoService, PhotoUploadBatch } from '../../../../../service/photo.service';
import { FilePickerService, FilePickOptions } from '../../../../../service/file-picker.service';
import { DataLoading } from '../../../../../models/DataLoading.interface';
import { LogEntryPhoto } from '../../../../../models/logs/photos/LogEntryPhoto.interface';
import { PhotoMetadata } from '../../../../../models/logs/photos/PhotoMetadata';
import { EntraService } from '../../../../../service/entra.service';
import { AssetsDataService } from '../../../../../service/assets-data.service';
import {
  LOG_PHOTO_SECTIONS,
  LogPhotoSection,
  LogPhotoSectionKey,
  nextPhotoIndex,
  photoFileName,
  sectionKeyForPhotoName,
  uploadKindForSection,
} from '../../../../../domain/receiving-log/rules/photo-sections.rules';


/** Photos staged for one section, and whether a capture is still processing. */
interface SectionBucket {
  photos: LogEntryPhoto[];
  adding: boolean;
}

@Component({
  selector: 'app-log-photo-manager',
  templateUrl: './log-photo-manager.component.html',
  styleUrls: ['./log-photo-manager.component.css'],
})
export class LogPhotoManagerComponent implements OnInit, OnDestroy{

  @Input() logId: number = 0;
  @Input() poNumber: string = '';
  @Output() closeModal = new EventEmitter<void>();


  public logRowPhotosSubscription!: Subscription;
  public logRowPhotos: PhotoMetadata[] = [];
  private dataLoadingSubscription!: Subscription;
  public dataLoading: DataLoading = { isLoading: false, message: '' };

  /**
   * Mobile device (not merely a narrow window) — chooses the capture affordance
   * per section, camera vs file picker, exactly as the mobile flow's photos step
   * does. Drag-and-drop stays available either way.
   */
  public isMobile: boolean = false;

  public readonly sections: readonly LogPhotoSection[] = LOG_PHOTO_SECTIONS;

  // The editor lays the sections out in three bands, keeping their declared
  // order: the full-width ones before the shipment categories (the packing
  // slip), the categories themselves as one side-by-side row, then the
  // full-width ones after (extras).
  private get _firstRowIndex(): number {
    return this.sections.findIndex(s => !s.wide);
  }
  private get _lastRowIndex(): number {
    return this.sections.map(s => s.wide).lastIndexOf(false);
  }
  public get leadingSections(): LogPhotoSection[] {
    // No categories at all (a config change): every section is full-width, so
    // they all belong to this band rather than disappearing.
    const firstRow = this._firstRowIndex;
    return firstRow < 0 ? [...this.sections] : this.sections.slice(0, firstRow);
  }
  public get rowSections(): LogPhotoSection[] {
    return this.sections.filter(s => !s.wide);
  }
  public get trailingSections(): LogPhotoSection[] {
    return this._lastRowIndex < 0 ? [] : this.sections.slice(this._lastRowIndex + 1);
  }

  /**
   * Photos staged per section, keyed the same way the mobile flow keys its photo
   * slots. Local to this editor rather than PhotoService.logEntryphotos$: that
   * subject is also filled with a log's *existing* photos when the email preview
   * opens, which would otherwise surface them here as pending uploads.
   */
  public buckets: Record<LogPhotoSectionKey, SectionBucket> = {
    'packing-slip':   { photos: [], adding: false },
    'shipping-label': { photos: [], adding: false },
    'parts-materials':{ photos: [], adding: false },
    'full-shipment':  { photos: [], adding: false },
    'extra-photo':    { photos: [], adding: false },
  };

  public userName: string = '';


  constructor(
    private _toastService: ToastService,
    private _screenSizeService: ScreenSizeService,
    private _photoService: PhotoService,
    private _picker: FilePickerService,
    private _entraService: EntraService,
    private _assetsDataService: AssetsDataService
  ) {
  }

  ngOnInit(): void {
    this.userName = this._entraService.UserName
    this.isMobile = this._screenSizeService.isMobile.getValue();

    this.logRowPhotosSubscription = this._photoService.logRowPhotos$.subscribe((photos: PhotoMetadata[]) => {
      this.logRowPhotos = photos;
    });

    this.dataLoadingSubscription = this._photoService.dataLoading.subscribe((dataLoading: DataLoading) => {
      this.dataLoading = dataLoading;
    });
  }

  ngOnDestroy(): void {
    if (this.logRowPhotosSubscription) {
      this.logRowPhotosSubscription.unsubscribe();
    }
    if (this.dataLoadingSubscription) {
      this.dataLoadingSubscription.unsubscribe();
    }
  }

  /**
   * A section's staged photos. The card markup is an <ng-template>, whose context
   * is untyped, so it reaches its bucket through here rather than indexing the
   * record with an `any` key.
   */
  public bucketFor(key: LogPhotoSectionKey): SectionBucket {
    return this.buckets[key];
  }

  /** Total staged across every section — gates the upload button and the count. */
  public get pendingCount(): number {
    return this.sections.reduce((total, s) => total + this.buckets[s.key].photos.length, 0);
  }

  /** How many photos the log already holds in a section (shown on its card). */
  public existingCountFor(key: LogPhotoSectionKey): number {
    return this.logRowPhotos.filter(
      (p) => sectionKeyForPhotoName(p.blobName) === key
    ).length;
  }

  // Capture — one add tile plus a drop target per section, the same pair the
  // mobile flow's photos step uses. Camera on mobile, file dialog on desktop;
  // both run every file through PhotoService.buildPhotosFromFile (resize, PDF
  // page split, compression) before it is staged. The picker call must stay
  // synchronous inside the click handler or mobile browsers suppress it.

  public addToSection(key: LogPhotoSectionKey): void {
    if (this.buckets[key].adding) { return; }
    this._picker.pick(this._pickOptions(this.isMobile), files => this._ingest(key, files));
  }

  /** Drag-and-drop onto a section's grid. */
  public onSectionFilesDropped(key: LogPhotoSectionKey, files: File[]): void {
    const { accepted, rejected } = this._picker.filterDropped(files, { allowPdf: true });
    if (rejected.length > 0) {
      this._toastService.showWarningMessage(
        `Skipped ${rejected.length} file(s) that are not images or PDFs`
      );
    }
    if (accepted.length === 0) { return; }
    this._ingest(key, accepted);
  }

  private async _ingest(key: LogPhotoSectionKey, files: File[]): Promise<void> {
    const bucket = this.buckets[key];
    const section = this.sections.find(s => s.key === key)!;
    bucket.adding = true;
    try {
      for (const file of files) {
        // Returns [] (and toasts) for a file it cannot use, so one bad file does
        // not abandon the rest of the selection. A PDF yields one photo per page.
        const built = await this._photoService.buildPhotosFromFile(file);
        for (const photo of built) {
          photo.name = photoFileName(section.fileLabel, this._nextIndexFor(section), this._photoExt(file));
          bucket.photos = [...bucket.photos, photo];
        }
      }
    } finally {
      bucket.adding = false;
    }
  }

  /**
   * Numbers a new photo past everything already using this section's prefix —
   * the photos stored on the log as well as the ones staged here, since both end
   * up in the same blob container.
   */
  private _nextIndexFor(section: LogPhotoSection): number {
    const taken = [
      ...this.logRowPhotos.map(p => p.blobName),
      ...this.buckets[section.key].photos.map(p => p.name),
    ];
    return nextPhotoIndex(taken, section.fileLabel);
  }

  // Staged photos open in the same full-screen viewer the grids use, paging
  // through the section they were added to.
  public isViewerOpen: boolean = false;
  public viewerPhotos: LogEntryPhoto[] = [];
  public viewerIndex: number = 0;

  public openPendingViewer(key: LogPhotoSectionKey, index: number): void {
    this.viewerPhotos = this.buckets[key].photos;
    this.viewerIndex = index;
    this.isViewerOpen = true;
  }

  public closePendingViewer(): void {
    this.isViewerOpen = false;
  }

  /** Drops a staged photo. Its number is left retired rather than reused. */
  public removePending(key: LogPhotoSectionKey, index: number): void {
    this.buckets[key].photos = this.buckets[key].photos.filter((_, i) => i !== index);
  }

  /**
   * Picker configuration. Camera capture is single-shot and images only; the file
   * dialog — desktop's only option, and a choice on mobile — takes several files
   * and PDFs at once.
   */
  private _pickOptions(useCamera: boolean): FilePickOptions {
    return useCamera ? { camera: true } : { multiple: true, allowPdf: true };
  }

  /**
   * Extension for a built photo: from the source name for an image, forced to jpg
   * for a PDF, whose pages are rasterized on the way in.
   */
  private _photoExt(file: File): string {
    if (file.type === 'application/pdf') { return 'jpg'; }
    const fromName = file.name?.split('.').pop()?.toLowerCase();
    if (fromName && fromName !== file.name?.toLowerCase()) { return fromName; }
    return 'jpg';
  }

  // Handle existing photo deletion
deleteExistingPhoto(index: number) {
  if (!confirm('Are you sure you want to delete this existing photo from the log entry?')) {
    return;
  }
  const logRowPhotos = [...this.logRowPhotos];
  const onSuccessFunction = () => {
    logRowPhotos.splice(index, 1);
    this._photoService.updateLogRowPhotos(logRowPhotos);

  }
  this._photoService.deletePhoto(this.logRowPhotos[index].id, onSuccessFunction);

  }

  public deleteAllPending(): void {
    if (!confirm('Are you sure you want to discard all the photos you just added?')) {
      return;
    }
    for (const section of this.sections) {
      this.buckets[section.key].photos = [];
    }
  }

  deleteAllExistingPhotos() {

      if (!confirm('Are you sure you want to delete all existing photos from this log entry?')) {
        return;
      }
      const onSuccessFunction = () => {
      }
      this._photoService.deleteAllPhotos(this.logId, onSuccessFunction);

  }

  /**
   * Uploads the staged photos, one request per kind: the packing-slip pages as
   * `kind=Slip`, everything else as ordinary proof photos with no kind (the
   * endpoint's default). The split is required — `kind` applies to a whole
   * request, since `files` is one flat field there.
   *
   * Within a request the names carry which section each photo belongs to, so
   * proof photos from the three categories and extras can share one batch.
   */
  async uploadNewPhotos() {
    const batches: PhotoUploadBatch[] = [];
    const proofPhotos: LogEntryPhoto[] = [];
    for (const section of this.sections) {
      const photos = this.buckets[section.key].photos;
      if (photos.length === 0) { continue; }
      const kind = uploadKindForSection(section.key);
      if (kind) {
        batches.push({ photos, kind });
      } else {
        proofPhotos.push(...photos);
      }
    }
    if (proofPhotos.length > 0) {
      batches.push({ photos: proofPhotos });
    }
    if (batches.length === 0) { return; }
    // addPhotos re-fetches the log's photos itself on success, so this only has
    // to clear what was staged.
    const onSuccessFunction = () => {
      for (const section of this.sections) {
        this.buckets[section.key].photos = [];
      }
    }
    this._photoService.addPhotoBatches(this.logId, this.poNumber, batches, this.userName, onSuccessFunction);
  }

}
